import { ChevronRight, Folder } from "@procore/core-icons";
import type { TreeRowProps } from "@procore/core-react";
import { Box, Tooltip, Tree, TreeDefaultNode, Typography } from "@procore/core-react";
import { useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { log } from "utils/log";
import { highlightText } from "utils/textHighlight";

import { selectMiniMapDrawings, selectMiniMapLevels, selectTempDrawingId, setTempDrawingId } from "./minimapSlice";
import { formatLevelDisplayName } from "./miniMapUtils";

const l = log.miniMap.levelsTree;

type LevelNode = { id: string; name: string; tooltip?: string; parentId: string | null; type: string };

function HighlightedTreeRow({
    node,
    getLabel,
    getIcon,
    isExpandable,
    isExpanded,
    isSelected,
    isSelectable,
    handleSelection,
    handleExpansion,
    searchTerm,
}: TreeRowProps & { searchTerm: string }) {
    const label = getLabel(node);
    const lowerTerm = searchTerm.trim().toLowerCase();

    const handleClick = () => {
        if (isExpandable(node)) {
            handleExpansion(node);
        }
        if (isSelectable(node)) {
            handleSelection(node);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
        }
    };

    const isInteractive = isSelectable(node) || isExpandable(node);

    const tooltipText = (node as unknown as LevelNode).tooltip;

    const row = (
        <Box
            tabIndex={isInteractive ? 0 : -1}
            aria-expanded={isExpandable(node) ? isExpanded(node) : undefined}
            aria-selected={isSelected(node)}
            aria-label={label}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            display="flex"
            alignItems="center"
            style={{
                paddingLeft: 24 + node.level * 32,
                paddingRight: 12,
                height: 36,
                cursor: isInteractive ? "pointer" : "default",
                backgroundColor: isSelected(node) ? "rgba(0, 120, 209, 0.08)" : "transparent",
                userSelect: "none",
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                style={{
                    width: 24,
                    height: 24,
                }}
            >
                {isExpandable(node) && (
                    <ChevronRight
                        style={{
                            width: 16,
                            height: 16,
                            transition: "transform 150ms ease",
                            transform: isExpanded(node) ? "rotate(90deg)" : "rotate(0deg)",
                        }}
                    />
                )}
            </Box>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                marginLeft="xs"
                marginRight="sm"
                style={{
                    width: 24,
                    height: 24,
                }}
            >
                {getIcon(node)}
            </Box>
            <Typography intent="body" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {lowerTerm ? highlightText(label, lowerTerm) : label}
            </Typography>
        </Box>
    );

    if (tooltipText) {
        return (
            <Tooltip overlay={tooltipText} trigger="hover" hideDelay={0} showDelay={100}>
                {row}
            </Tooltip>
        );
    }

    return row;
}

interface LevelsTreeWebProps {
    searchTerm: string;
    onDrawingChange?: () => void;
}

export function LevelsTreeWeb({ searchTerm, onDrawingChange }: LevelsTreeWebProps) {
    const [levelData, setLevelData] = useState<LevelNode[]>([]);
    const drawings = useAppSelector(selectMiniMapDrawings);
    const levels = useAppSelector(selectMiniMapLevels);
    const tempDrawingId = useAppSelector(selectTempDrawingId);
    const dispatch = useAppDispatch();

    useEffect(() => {
        const levelTreeData: LevelNode[] = [];
        levels.forEach((level) => {
            const { displayName, tooltip } = formatLevelDisplayName(level.name);
            levelTreeData.push({
                id: level.id,
                name: displayName,
                tooltip,
                parentId: null,
                type: "branch",
            });
            level.drawingIds.forEach((drawingId) => {
                levelTreeData.push({
                    id: drawingId,
                    name: drawings[drawingId].name,
                    parentId: level.id,
                    tooltip: drawings[drawingId].name,
                    type: "leaf",
                });
            });
        });
        setLevelData(levelTreeData);
    }, [levels, drawings]);

    const { filteredLevelData, expandedIds } = useMemo(() => {
        if (!searchTerm.trim()) {
            return {
                filteredLevelData: levelData,
                expandedIds: [],
            };
        }

        const lowerSearchTerm = searchTerm.toLowerCase();

        const matchingLeaves = levelData.filter(
            (node) => node.type === "leaf" && node.name.toLowerCase().includes(lowerSearchTerm),
        );

        const matchingBranches = levelData.filter(
            (node) => node.type === "branch" && node.name.toLowerCase().includes(lowerSearchTerm),
        );

        const matchingBranchIds = matchingBranches.map((branch) => branch.id);

        // Children of matching branches
        const childrenOfMatchingBranches = levelData.filter(
            (node) => node.type === "leaf" && node.parentId !== null && matchingBranchIds.includes(node.parentId),
        );

        // Parent branches of matching leaves (that didn't already match by name)
        const parentIdsOfMatchingLeaves = matchingLeaves
            .map((leaf) => leaf.parentId)
            .filter((id) => id !== null && !matchingBranchIds.includes(id));

        const parentBranchesOfMatchingLeaves = levelData.filter(
            (node) => node.type === "branch" && parentIdsOfMatchingLeaves.includes(node.id),
        );

        const allBranches = [...matchingBranches, ...parentBranchesOfMatchingLeaves];
        const allLeaves = [...childrenOfMatchingBranches, ...matchingLeaves].filter(
            (node, index, self) => self.findIndex((n) => n.id === node.id) === index,
        );

        const expandedBranchIds = allBranches.map((b) => b.id);

        return {
            filteredLevelData: [...allBranches, ...allLeaves],
            expandedIds: expandedBranchIds,
        };
    }, [levelData, searchTerm]);

    if (filteredLevelData.length === 0) {
        return null;
    }

    return (
        <Tree
            options={filteredLevelData}
            expanded={expandedIds}
            selected={tempDrawingId ? [tempDrawingId] : []}
            multiple={false}
            onSelect={(nodes) => {
                if (nodes.length === 0) {
                    // This seems to happen when focusing an unselected node
                    // with tab and then pressing enter while holding shift.
                    // onSelect is called twice, once with an empty array and
                    // once with the selected node, so it still works, but we
                    // have to guard against the empty array case.
                    //
                    // Space, Shift+Space, and Enter without Shift work as
                    // expected.
                    //
                    // Seems like an issue with core-react#Tree
                    l.warn("No nodes selected");
                    return;
                }
                const node = nodes[0] as TreeDefaultNode;
                if (node.type !== "leaf") {
                    return;
                }
                const nodeId = node.id as string;
                if (nodeId === tempDrawingId) {
                    return;
                }
                dispatch(setTempDrawingId(nodeId));
                onDrawingChange?.();
            }}
            getIcon={(node) => {
                if ((node as TreeDefaultNode).type === "leaf") {
                    return (
                        <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M0 0V18H20V7H15V0H0ZM8.5 4.5V2.5H12.5V9.5H17.5V15.5H8.5V12H6V15.5H2.5V9.5H8.5V7H2.5V2.5H6V4.5H8.5Z"
                                fill="#6A767C"
                            />
                        </svg>
                    );
                }
                return <Folder style={{ width: 24, height: 24 }} />;
            }}
            rowRenderer={(props) => <HighlightedTreeRow {...props} searchTerm={searchTerm} />}
        />
    );
}
